import { Check, ChevronDown, Monitor, Moon, Palette, Sun } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ThemePreference } from "../lib/theme";
import { triggerHaptic } from "../lib/telegram";
import { useTheme } from "./ThemeProvider";

const themeOptions: Array<{
  value: ThemePreference;
  label: string;
  description: string;
  icon: typeof Sun;
}> = [
  { value: "light", label: "Светлая", description: "Светлый фон интерфейса", icon: Sun },
  { value: "dark", label: "Тёмная", description: "Комфортно при слабом свете", icon: Moon },
  { value: "system", label: "Как на устройстве", description: "Следовать настройкам телефона", icon: Monitor },
];

const themeLabels: Record<ThemePreference, string> = {
  light: "Светлая",
  dark: "Тёмная",
  system: "Системная",
};

export function ThemeSwitcher({ variant }: { variant: "floating" | "sidebar" }) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { preference, setPreference } = useTheme();

  useEffect(() => {
    if (!isOpen) return;

    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isOpen]);

  return (
    <div ref={wrapperRef} className={`theme-switcher theme-switcher--${variant}`}>
      <button
        type="button"
        className="theme-switcher__trigger"
        aria-label={`Тема интерфейса: ${themeLabels[preference]}`}
        title="Сменить тему интерфейса"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => {
          triggerHaptic("open");
          setIsOpen((open) => !open);
        }}
      >
        <span className="theme-switcher__trigger-icon">
          <Palette size={variant === "sidebar" ? 18 : 20} aria-hidden="true" />
        </span>
        {variant === "sidebar" && (
          <>
            <span className="theme-switcher__trigger-copy">
              <small>Тема интерфейса</small>
              <strong>{themeLabels[preference]}</strong>
            </span>
            <ChevronDown size={16} aria-hidden="true" />
          </>
        )}
      </button>

      {isOpen && (
        <div className="theme-switcher__menu" role="menu" aria-label="Выбор темы">
          <div className="theme-switcher__menu-title">Оформление</div>
          {themeOptions.map(({ value, label, description, icon: Icon }) => (
            <button
              key={value}
              type="button"
              role="menuitemradio"
              aria-checked={preference === value}
              className={`theme-switcher__option ${preference === value ? "is-selected" : ""}`}
              onClick={() => {
                setPreference(value);
                triggerHaptic("open");
                setIsOpen(false);
              }}
            >
              <span className="theme-switcher__option-icon">
                <Icon size={18} aria-hidden="true" />
              </span>
              <span>
                <strong>{label}</strong>
                <small>{description}</small>
              </span>
              {preference === value && <Check size={17} aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
